import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  MenuItem,
  FormControl,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { claimTableSession, getAllTavoli } from '../APIs/TablesAPI';
import { useTheme } from '@mui/material/styles';
import Select from '../components/SearchableSelect';
import ModalTavolo from './ModalTavolo';
import ModalSala from './ModalSala';
import { getAllStanze } from '../APIs/RoomsAPI';
import AddIcon from '@mui/icons-material/Add';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { useNavigate } from 'react-router-dom';
import { GridViewOutlined, PinDropOutlined, Search, VisibilityOutlined } from '@mui/icons-material';
import VistaStandard from '../components/VistaStandard';
import VistaDettagliata from '../components/VistaDettagliata';
import { getAllPrenotazioni } from '../APIs/ReservationsAPI';
import { getAllOrdini } from '../APIs/OrdersAPI';
import SelezioneComanda from './SelezionaComande';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import ModalEditSala from './ModalEditSala';
import ModalDeleteSala from './ModalDeleteSala';
import { getTenantTranslation } from '../utils/getTenantTranslation';
import { useSnackbar } from '../contexts/SnackBarContext';
import { getTenantSettings } from '../APIs/TenantSettingsAPI';
import { dateTimeWithLocalOffset, getSpaAppointments, getSpaCabinBoard, getSpaPlanner } from '../APIs/SpaAPI';

const BACKEND_TABLE_VIEW_TO_UI = {
  STANDARD: 'standard',
  DETAILED: 'dettagliata',
};

const NON_RESERVATION_TABLE_KEY_PREFIX = "nonReservationTable";
const getNonReservationTableStorageKey = (tenant) => `${NON_RESERVATION_TABLE_KEY_PREFIX}:${tenant || "default"}`;

const getNonReservationTableMarkers = (tenant) => {
  try {
    const raw = localStorage.getItem(getNonReservationTableStorageKey(tenant));
    const markers = JSON.parse(raw || "[]");
    return new Set((Array.isArray(markers) ? markers : []).map(String));
  } catch {
    return new Set();
  }
};

const orderBelongsToTable = (order, tableId) => {
  const normalizedTableId = Number(tableId);
  if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return false;
  if (Number(order?.tableId ?? order?.table?.id) === normalizedTableId) return true;
  return (Array.isArray(order?.orderTables) ? order.orderTables : []).some((link) => (
    Number(link?.tableId ?? link?.table?.id) === normalizedTableId
  ));
};

const getOrderGuestCountForTable = (orders = [], tableId) => (
  (Array.isArray(orders) ? orders : []).reduce((sum, order) => {
    const links = Array.isArray(order?.orderTables) ? order.orderTables : [];
    const matchedLink = links.find((link) => Number(link?.tableId ?? link?.table?.id) === Number(tableId));
    return sum + Math.max(0, Number(matchedLink?.guestCount) || 0);
  }, 0)
);

const FINAL_SPA_APPOINTMENT_STATUSES = new Set(['CANCELLED', 'NO_SHOW', 'COMPLETED']);

const normalizeResourceName = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\bcabin\b/g, 'cab')
    .replace(/[^a-z0-9]/g, '')
);

const isSpaCabinName = (name) => /^cab(?:in)?\s*\d+/i.test(String(name || '').trim());

const hasChildCabins = (room, tables = []) => {
  const roomId = Number(room?.id);
  if (Array.isArray(room?.tables) && room.tables.length > 0) return true;
  return tables.some((item) => Number(item?.roomId) === roomId);
};

const collectNumericIds = (...values) => (
  values.flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0)
);

const dateKey = (date = new Date()) => {
  const pad = (item) => String(item).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const addDays = (value, amount) => {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + amount);
  return dateKey(date);
};

const localParts = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  const pad = (item) => String(item).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const displaySpaCustomerName = (customer, fallback = '-') => {
  const name = [customer?.name, customer?.surname].filter(Boolean).join(' ').trim();
  return name || fallback;
};

const flattenSpaBoardAppointments = (board) => (
  (Array.isArray(board?.cabins) ? board.cabins : []).flatMap((cabin) => (
    (Array.isArray(cabin.appointments) ? cabin.appointments : []).map((appointment) => {
      const start = localParts(appointment.startAt);
      const end = localParts(appointment.endAt);
      return {
        id: appointment.id,
        date: start.date,
        startTime: start.time,
        endTime: end.time,
        resourceId: Number(cabin.id),
        resourceCombination: { cabinIds: [Number(cabin.id)] },
        assignedResources: { cabins: [{ id: Number(cabin.id), name: cabin.name }] },
        clientName: displaySpaCustomerName(appointment.customer),
        treatmentName: appointment.treatment?.name || '-',
        commercialStatus: appointment.commercialStatus,
        operationalStatus: appointment.operationalStatus,
        partySize: 1,
      };
    })
  ))
);

const mergeSpaAppointments = (...sources) => {
  const byId = new Map();
  sources.flat().forEach((appointment) => {
    if (!appointment?.id) return;
    byId.set(Number(appointment.id), {
      ...(byId.get(Number(appointment.id)) || {}),
      ...appointment,
    });
  });
  return [...byId.values()];
};

const spaAppointmentUsesCabin = (appointment, cabin) => {
  const cabinName = normalizeResourceName(cabin?.name);
  const assignedCabins = Array.isArray(appointment?.assignedResources?.cabins)
    ? appointment.assignedResources.cabins
    : [];
  const assignedCabinNames = assignedCabins.map((item) => normalizeResourceName(item.name)).filter(Boolean);
  if (cabinName && assignedCabinNames.length) {
    return assignedCabinNames.includes(cabinName);
  }

  const cabinIdsToMatch = collectNumericIds(
    cabin?.id,
    cabin?.roomId,
    cabin?.tableId,
    cabin?.resourceId,
    cabin?.spaCabinProfile?.roomId,
    cabin?.room?.id
  );
  if (!cabinIdsToMatch.length && !cabinName) return false;
  if (cabinIdsToMatch.includes(Number(appointment?.resourceId))) return true;
  const cabinIds = Array.isArray(appointment?.resourceCombination?.cabinIds)
    ? appointment.resourceCombination.cabinIds
    : [];
  const preferredCabinIds = Array.isArray(appointment?.preferredResources?.cabinIds)
    ? appointment.preferredResources.cabinIds
    : [];
  if ([...cabinIds, ...preferredCabinIds].some((id) => cabinIdsToMatch.includes(Number(id)))) return true;
  const assignedCabinIds = assignedCabins.flatMap((item) => collectNumericIds(
    item?.id,
    item?.roomId,
    item?.resourceId,
    item?.tableId
  ));
  if (assignedCabinIds.some((id) => cabinIdsToMatch.includes(id))) return true;
  return Boolean(cabinName && assignedCabinNames.includes(cabinName));
};

const isLiveSpaAppointment = (appointment) => {
  const commercialStatus = String(appointment?.commercialStatus || appointment?.status || '').toUpperCase();
  const operationalStatus = String(appointment?.operationalStatus || '').toUpperCase();
  return !FINAL_SPA_APPOINTMENT_STATUSES.has(commercialStatus)
    && !FINAL_SPA_APPOINTMENT_STATUSES.has(operationalStatus);
};

const isSpaAppointmentInProgress = (appointment) => (
  String(appointment?.operationalStatus || '').toUpperCase() === 'IN_PROGRESS'
);

const currentSpaCabinBoardStatus = (cabin, now = new Date()) => {
  if (!cabin) return null;
  if (cabin.active === false) return 'OUT_OF_SERVICE';
  const instant = new Date(now).getTime();
  const interval = (cabin.statusIntervals || []).find((item) =>
    new Date(item.startAt).getTime() <= instant && instant < new Date(item.endAt).getTime());
  return interval?.status || 'AVAILABLE';
};

const getAppointmentClientName = (appointment, fallback) => {
  const value = String(appointment?.clientName || '').trim();
  return value && value !== '-' ? value : fallback;
};

const MappaTavoli = ({ compact = false }) => {
  const [tavoli, setTavoli] = useState([]);
  const [stanze, setStanze] = useState([]);
  const [prenotazioni, setPrenotazioni] = useState([]);
  const [spaAppointments, setSpaAppointments] = useState([]);
  const [spaBoardCabins, setSpaBoardCabins] = useState([]);
  const [ordini, setOrdini] = useState([]);
  const [openComanda, setOpenComanda] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTavolo, setSelectedTavolo] = useState(null);
  const [selectedStanzaId, setSelectedStanzaId] = useState(() => {
    const val = localStorage.getItem('selectedStanzaId');
    if (!val) return 'ALL';
    return val === 'ALL' ? 'ALL' : Number(val);
  });
  const [openSala, setOpenSala] = useState(false);
  const [openEditSala, setOpenEditSala] = useState(false);
  const [openDeleteSala, setOpenDeleteSala] = useState(false);
  const [openTavolo, setOpenTavolo] = useState(false);
  const [padding, setPadding] = useState(null);
  const [cercaTavolo, setCercaTavolo] = useState('');
  const [vistaCorrente, setVistaCorrente] = useState('standard');
  const [editTable, setEditTable] = useState(false);
  const [scrollTick, setScrollTick] = useState(0);
  const [spaReservationChoice, setSpaReservationChoice] = useState({
    open: false,
    tavolo: null,
    isReservationClient: true,
    partySize: 1,
  });
  const orderSentRef = useRef(false);
  const tableWasOccupiedRef = useRef(false);

  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const tenantType = localStorage.getItem('tenantType');
  const normalizedTenantType = String(tenantType || '').toUpperCase();
  const isConferenceTenant = normalizedTenantType === 'SALE_CONGRESSI';
  const isSpaTenant = normalizedTenantType === 'SPA';

  const navigate = useNavigate();

  const { t } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const sortTablesByRoomAndName = useCallback((items = []) => {
    return [...items].sort((a, b) => {
      const roomDiff = (a.roomId ?? 0) - (b.roomId ?? 0);
      if (roomDiff !== 0) return roomDiff;
      return String(a.name ?? "").localeCompare(String(b.name ?? ""), undefined, { numeric: true, sensitivity: "base" });
    });
  }, []);

  const statusColors = {
    light: {
      LIBERO: '#125605',
      IN_ATTESA: '#CC4B00',
      OCCUPATO: '#8A8A8A',
      RISERVATO: '#004394',
      SPA_NON_DISPONIBILE: '#E00C0C',
      'RITARDO LIEVE': '#CC4B00',
      'RITARDO GRAVE': '#E00C0C',
    },
    dark: {
      LIBERO: '#78BD6A',
      IN_ATTESA: '#F9934B',
      OCCUPATO: '#C9C9C9',
      RISERVATO: '#549AEF',
      SPA_NON_DISPONIBILE: '#FF5A5A',
      'RITARDO LIEVE': '#F9934B',
      'RITARDO GRAVE': '#FF8C8C',
    }
  };

  const handleScroll = () => {
    setScrollTick(t => t + 1);
  };

  const fetchAllData = useCallback(async () => {
    try {
      const today = dateKey();
      const [tavoliData, stanzeData, prenotazioniData, ordiniData, spaBoardData, spaPlannerData, spaAppointmentData] = await Promise.all([
        getAllTavoli(token, tenant, { noCache: true }),
        getAllStanze(token, tenant, { noCache: true }),
        isSpaTenant ? Promise.resolve([]) : getAllPrenotazioni(token, tenant),
        isSpaTenant ? Promise.resolve([]) : getAllOrdini(token, tenant, { noCache: true }),
        isSpaTenant ? getSpaCabinBoard({
          from: dateTimeWithLocalOffset(today, '00:00'),
          to: dateTimeWithLocalOffset(addDays(today, 1), '00:00'),
        }, token, tenant).catch(() => ({ cabins: [] })) : Promise.resolve({ cabins: [] }),
        isSpaTenant ? getSpaPlanner({ date: today, view: 'day' }, token, tenant).catch(() => ({ appointments: [] })) : Promise.resolve({ appointments: [] }),
        isSpaTenant ? getSpaAppointments(token, tenant).catch(() => []) : Promise.resolve([]),
      ]);

      const ordinati = [...tavoliData].sort((a, b) => a.id - b.id);

      function normalizeDate(date) {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
      }
      const oggi = normalizeDate(new Date());
      const ordiniFiltrati = [...ordiniData].filter((ordine) => {
        const createdAt = normalizeDate(new Date(ordine.createdAt));
        return createdAt.getTime() === oggi.getTime();
      });

      setTavoli(sortTablesByRoomAndName(ordinati));
      setStanze(stanzeData);
      setPrenotazioni(prenotazioniData);
      setSpaBoardCabins(Array.isArray(spaBoardData?.cabins) ? spaBoardData.cabins : []);
      setSpaAppointments(mergeSpaAppointments(
        flattenSpaBoardAppointments(spaBoardData),
        Array.isArray(spaPlannerData?.appointments) ? spaPlannerData.appointments : [],
        Array.isArray(spaAppointmentData) ? spaAppointmentData : []
      ));
      setOrdini(ordiniFiltrati);
      setLoading(false);
    } catch (error) {
      console.error('Errore:', error);
      setLoading(false);
    }
  }, [token, tenant, sortTablesByRoomAndName, isSpaTenant]);

  useEffect(() => {
    fetchAllData();
    const cleanup = SetupWSEventListener(fetchAllData);
    if (!compact) setPadding(16);
    else setPadding(0);
    return cleanup;
  }, [fetchAllData, compact]);

  useEffect(() => {
    let cancelled = false;

    const fetchDefaultMapView = async (silent = false) => {
      if (!token || !tenant) return;
      try {
        const settings = await getTenantSettings(token, tenant);
        if (cancelled) return;
        const nextView = isConferenceTenant
          ? 'standard'
          : BACKEND_TABLE_VIEW_TO_UI[settings?.defaultTableView] || 'standard';
        setVistaCorrente(nextView);
      } catch (err) {
        if (!silent && !cancelled) {
          showSnackbar(err?.message || 'Errore nel recupero impostazioni sistema', 'error');
        }
      }
    };

    fetchDefaultMapView();

    const bc = new BroadcastChannel('ws-events');
    const handler = ({ data }) => {
      if (data?.event === 'settings_updated') {
        fetchDefaultMapView(true);
      }
    };
    bc.addEventListener('message', handler);

    return () => {
      cancelled = true;
      bc.removeEventListener('message', handler);
      bc.close();
    };
  }, [token, tenant, showSnackbar, isConferenceTenant]);

  useEffect(() => {
    if (!isConferenceTenant || vistaCorrente === 'standard') return;
    setVistaCorrente('standard');
    setEditTable(false);
  }, [isConferenceTenant, vistaCorrente]);

  useEffect(() => {
    const handler = () => {
      orderSentRef.current = true;
      fetchAllData();
    };
    window.addEventListener('comanda-creata', handler);
    window.addEventListener('map-data-changed', handler);
    return () => {
      window.removeEventListener('comanda-creata', handler);
      window.removeEventListener('map-data-changed', handler);
    };
  }, [fetchAllData, compact]);

  function getDelayStatus(lastOrderAt) {
    if (!lastOrderAt) return null;
    const diffMin = (new Date() - new Date(lastOrderAt)) / 1000 / 60;
    if (diffMin > 10 && diffMin <= 60) return "RITARDO LIEVE";
    if (diffMin > 60) return "RITARDO GRAVE";
    return null;
  }

  const handleCloseComanda = async () => {
    setOpenComanda(false);
    orderSentRef.current = false;
    tableWasOccupiedRef.current = false;
    await fetchAllData();
  };

  const getTableBusyMessage = useCallback((session) => {
    return t("tableSession.inUse");
  }, [t]);

  const canOpenTableOrder = useCallback(async (tavolo) => {
    if (!tavolo?.id) return false;

    try {
      await claimTableSession(tavolo.id, token, tenant);
      const roomId = Number(tavolo.roomId);
      if (Number.isInteger(roomId) && roomId > 0) {
        setSelectedStanzaId(roomId);
        localStorage.setItem('selectedStanzaId', String(roomId));
      }
      return true;
    } catch (err) {
      const fallback = err?.status === 409 ? getTableBusyMessage(err) : (err?.message || String(err));
      showSnackbar(fallback, err?.status === 409 ? 'warning' : 'error');
      return false;
    }
  }, [token, tenant, showSnackbar, getTableBusyMessage]);

  const openSpaPlannerForCabin = useCallback((tavolo = null, mode = null) => {
    if (
      isSpaTenant
      && !mode
      && String(tavolo?.status || '').toUpperCase() === 'RISERVATO'
      && tavolo?.prenotazione?.id
    ) {
      const reservedPartySize = Number(tavolo?.prenotazione?.partySize || tavolo?.numeroOspiti || 1);
      setSpaReservationChoice({
        open: true,
        tavolo,
        isReservationClient: true,
        partySize: Number.isInteger(reservedPartySize) && reservedPartySize > 0 ? reservedPartySize : 1,
      });
      return;
    }

    const params = new URLSearchParams();
    const cabinResourceId = tavolo?.id;
    if (cabinResourceId) params.set('resourceId', String(cabinResourceId));
    if (tavolo?.id) params.set('tableId', String(tavolo.id));
    if (tavolo?.name) params.set('resourceName', tavolo.name);
    if (mode === 'reservationClient' && tavolo?.prenotazione?.id) {
      params.set('appointmentId', String(tavolo.prenotazione.id));
      setSpaReservationChoice({ open: false, tavolo: null, isReservationClient: true, partySize: 1 });
      navigate(`/spa/operations?${params.toString()}`);
      return;
    } else {
      params.set('new', '1');
      if (spaReservationChoice.partySize) params.set('partySize', String(spaReservationChoice.partySize));
      if (tavolo?.prenotazione?.id) {
        params.set('sourceAppointmentId', String(tavolo.prenotazione.id));
        params.set('clientMode', 'other');
      }
    }
    setSpaReservationChoice({ open: false, tavolo: null, isReservationClient: true, partySize: 1 });
    navigate(`/spa/planner?${params.toString()}`);
  }, [isSpaTenant, navigate, spaReservationChoice.partySize]);

  const continueSpaReservedCabinFlow = useCallback(() => {
    const mode = spaReservationChoice.isReservationClient ? 'reservationClient' : 'otherClient';
    openSpaPlannerForCabin(spaReservationChoice.tavolo, mode);
  }, [openSpaPlannerForCabin, spaReservationChoice]);

  const getColor = (status, lastOrderAt) => {
    const delayStatus = getDelayStatus(lastOrderAt);
    const effectiveStatus = delayStatus || (isSpaTenant && status === 'NON_DISPONIBILE' ? 'SPA_NON_DISPONIBILE' : status);
    const currentPalette = statusColors[theme.palette.mode] || {};
    return currentPalette[effectiveStatus] || '#e0e0e0';
  };

  const getPrenotazioneImminente = (tableId) => {
    const now = new Date();
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    const domani = new Date(oggi);
    domani.setDate(domani.getDate() + 1);

    const prenotazioniTavoloOggi = prenotazioni.filter(p => {
      const dataPrenotazione = new Date(p.startDate);
      const finePrenotazione = p?.endDate ? new Date(p.endDate) : null;
      const isConfirmed = p.status === 'CONFIRMED';
      const belongsToTable = p.tableReservations?.some(
        tr => tr.tableId === tableId || tr.table?.id === tableId
      );
      const isToday = dataPrenotazione >= oggi && dataPrenotazione < domani;
      const isNotExpired = finePrenotazione ? finePrenotazione > now : dataPrenotazione > now;
      return isConfirmed && belongsToTable && isToday && isNotExpired;
    });

    if (prenotazioniTavoloOggi.length === 0) return null;
    return prenotazioniTavoloOggi.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];
  };

  const getSpaAppointmentForCabin = (cabin) => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const cabinAppointmentsToday = spaAppointments
      .filter((appointment) => {
        if (!isLiveSpaAppointment(appointment)) return false;
        if (!spaAppointmentUsesCabin(appointment, cabin)) return false;
        const startAt = new Date(`${appointment.date}T${appointment.startTime || '00:00'}`);
        const endAt = new Date(`${appointment.date}T${appointment.endTime || appointment.startTime || '00:00'}`);
        if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) return false;
        return startAt < tomorrow && endAt > today && endAt > now;
      })
      .map((appointment) => {
        const startAt = new Date(`${appointment.date}T${appointment.startTime || '00:00'}`);
        const endAt = new Date(`${appointment.date}T${appointment.endTime || appointment.startTime || '00:00'}`);
        return {
          ...appointment,
          _startAt: startAt,
          _endAt: endAt,
          _activeNow: isSpaAppointmentInProgress(appointment) || (startAt <= now && endAt > now),
        };
      });

    if (cabinAppointmentsToday.length === 0) return null;
    return cabinAppointmentsToday.sort((a, b) => {
      if (a._activeNow !== b._activeNow) return a._activeNow ? -1 : 1;
      return a._startAt - b._startAt;
    })[0];
  };

  const getSpaBoardCabinForTable = (tavolo) => {
    const tableName = normalizeResourceName(tavolo?.name);
    return (spaBoardCabins || []).find((cabin) => {
      if (tableName && normalizeResourceName(cabin?.name) === tableName) return true;
      return collectNumericIds(cabin?.id, cabin?.roomId).some((id) => collectNumericIds(tavolo?.id, tavolo?.tableId).includes(id));
    }) || null;
  };

  const nonReservationTableMarkers = useMemo(
    () => getNonReservationTableMarkers(tenant),
    [tenant]
  );

  const tavoliDettagliati = tavoli.map(tavolo => {
    const prenotazione = getPrenotazioneImminente(tavolo.id);
    const spaAppointment = isSpaTenant ? getSpaAppointmentForCabin(tavolo) : null;
    const spaBoardCabin = isSpaTenant ? getSpaBoardCabinForTable(tavolo) : null;
    const spaCabinStatus = currentSpaCabinBoardStatus(spaBoardCabin);
    const ordiniTavolo = ordini.filter(o => orderBelongsToTable(o, tavolo.id));
    const hasActiveOrder = ordiniTavolo.some((o) => !['PAID', 'CANCELLED'].includes(o.status));
    const isNonReservationClient = !!prenotazione
      && nonReservationTableMarkers.has(`${Number(tavolo.id)}:${Number(prenotazione.id)}`);

    const derivedStatus = (() => {
      if (isSpaTenant) {
        if (spaAppointment?._activeNow) return 'OCCUPATO';
        if (spaAppointment) return 'RISERVATO';
        if (['OUT_OF_SERVICE', 'MAINTENANCE', 'CLEANING'].includes(spaCabinStatus)) return 'NON_DISPONIBILE';
        if (spaCabinStatus === 'PREPARATION') return 'RISERVATO';
        if (tavolo.status === 'NON_DISPONIBILE') return 'NON_DISPONIBILE';
        return 'LIBERO';
      }
      // a table can be OCCUPATO without an open order because staff may be in the process of creating one
      // if (tavolo.status === 'OCCUPATO' && !hasOpenOrder) return 'LIBERO';   
      // ── stale RISERVATO from a previous day with no active reservation today → reset
      if (hasActiveOrder) return 'OCCUPATO';
      if (tavolo.status === 'RISERVATO' && !prenotazione) return 'LIBERO';
      if (tavolo.status === 'LIBERO' && prenotazione) return 'RISERVATO';
      if (tavolo.status === 'NON_DISPONIBILE' && prenotazione) return 'RISERVATO';
      return tavolo.status;
    })();

    const reservationGuests = Number(prenotazione?.guests ?? 0);
    const spaGuests = Number(spaAppointment?.partySize ?? 0);
    const hasReservationGuests = Number.isFinite(reservationGuests) && reservationGuests > 0;
    const baseTakenSeats = Number(tavolo.takenSeats ?? 0);
    const linkedOrderGuests = getOrderGuestCountForTable(ordiniTavolo, tavolo.id);
    const derivedTakenSeats = (derivedStatus === 'LIBERO')
      ? 0
      : (isSpaTenant
        ? (Number.isFinite(spaGuests) && spaGuests > 0 ? spaGuests : 1)
        : (linkedOrderGuests > 0 ? linkedOrderGuests : (hasReservationGuests ? reservationGuests : baseTakenSeats)));

    const prodottiAggregati = ordiniTavolo.flatMap(o => o.orderProducts || [])
      .reduce((acc, prodotto) => {
        const key = prodotto.productId;
        if (!acc[key]) {
          acc[key] = { ...prodotto.product, quantity: prodotto.quantity };
        } else {
          acc[key].quantity += prodotto.quantity;
        }
        return acc;
      }, {});

    const reservationClientName = prenotazione?.client
      ? `${prenotazione.client.name || ""} ${prenotazione.client.surname || ""}`.trim()
      : "";
    const reservationHeliteName = [
      prenotazione?.heliteHolderName,
      prenotazione?.heliteRoomNumber ? `Camera ${prenotazione.heliteRoomNumber}` : null,
    ].filter(Boolean).join(" - ");

    return {
      ...tavolo,
      status: derivedStatus,
      spaCabinStatus,
      takenSeats: derivedTakenSeats,
      prenotazione: isSpaTenant ? spaAppointment : prenotazione,
      hasPrenotazione: isSpaTenant ? !!spaAppointment : !!prenotazione,
      nomeCliente: isNonReservationClient
        ? t("checkout_page.notBookedClient")
        : spaAppointment
        ? getAppointmentClientName(spaAppointment, t('reservations_details.noClient'))
        : prenotazione
        ? reservationClientName || reservationHeliteName || prenotazione.eventName || t('reservations_details.noClient')
        : t('reservations_details.noClient'),
      oraPrenotazione: spaAppointment ? spaAppointment.startTime : prenotazione
        ? new Date(prenotazione.startDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', hour12: false })
        : '—',
      ordersCreatedAt: ordiniTavolo.filter(ord => ord.createdAt),
      numeroOspiti: spaAppointment ? spaAppointment.partySize : (prenotazione ? prenotazione.guests : '—'),
      notePrenotazione: spaAppointment ? spaAppointment.treatmentName : (prenotazione ? prenotazione.note : null),
      clientId: spaAppointment ? spaAppointment.clientId : (prenotazione ? prenotazione.clientId : null),
      ordini: Object.values(prodottiAggregati),
    };
  });

  const activeStanze = useMemo(
    () => stanze.filter((sala) => {
      if (sala?.active === false) return false;
      if (!isSpaTenant) return true;
      return hasChildCabins(sala, tavoli) && !isSpaCabinName(sala?.name);
    }),
    [isSpaTenant, stanze, tavoli]
  );
  const activeRoomIds = useMemo(
    () => new Set(activeStanze.map((sala) => sala.id)),
    [activeStanze]
  );

  useEffect(() => {
    if (loading) return;
    if (selectedStanzaId === 'ALL') return;
    if (activeRoomIds.has(selectedStanzaId)) return;
    setSelectedStanzaId('ALL');
    localStorage.setItem('selectedStanzaId', 'ALL');
  }, [loading, selectedStanzaId, activeRoomIds]);

  useEffect(() => {
    if (isConferenceTenant || vistaCorrente !== 'dettagliata') return;
    if (selectedStanzaId !== 'ALL') return;
    const firstActiveRoomId = activeStanze[0]?.id;
    if (!firstActiveRoomId) return;
    setSelectedStanzaId(firstActiveRoomId);
    localStorage.setItem('selectedStanzaId', firstActiveRoomId);
  }, [isConferenceTenant, vistaCorrente, selectedStanzaId, activeStanze]);

  const tavoliInSaleAttive = tavoliDettagliati.filter((t) => activeRoomIds.has(t.roomId));
  const tavoliFiltrati = selectedStanzaId !== 'ALL'
    ? tavoliInSaleAttive.filter(t => t.roomId === selectedStanzaId)
    : tavoliInSaleAttive;

  const tavoliFiltratiPerNome = tavoliFiltrati.filter(t =>
    t.name.toLowerCase().includes(cercaTavolo.toLowerCase())
  );

  const fetchTavoli = async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const tavoliDB = await getAllTavoli(token, tenant, { noCache: true });
    setTavoli(sortTablesByRoomAndName(tavoliDB));
  };

  const fetchStanze = async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const saleDB = await getAllStanze(token, tenant, { noCache: true });
    setStanze(saleDB);
  };

  const handleSearch = (e) => {
    setCercaTavolo(e.target.value);
  };

  if (compact) {
    return (
      <Box sx={{
        border: '1px solid #CCC',
        borderRadius: '8px',
        padding: { xs: 1.25, md: '1.719vw' },
        height: { xs: 'auto', md: 'calc(100vh - 220px)' },
        minHeight: { xs: 0, md: '633px' },
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box display="flex" alignItems="center" width={"100%"} justifyContent={"space-between"} pb={"16px"}>
          <Box display="flex" justifyContent={"space-between"} width={"100%"} gap={2}>
            <Typography gutterBottom sx={{
              fontFamily: "Helvetica Neue", fontSize: "20px", fontStyle: "normal",
              fontWeight: "700", lineHeight: "175%", letterSpacing: "0.4px",
            }}>
              {getTenantTranslation(t, tenantType, 'tablesMap')}
            </Typography>
            <Button
              color='secondary'
              variant="contained"
              sx={{
                backgroundColor: theme.palette.mode === 'dark' ? '#549AEF' : '#0052cc',
                '&:hover': { opacity: 0.9, backgroundColor: theme.palette.customPagination.secondary },
              }}
              onClick={() => isSpaTenant ? navigate('/spa/planner') : navigate('/prenotazione')}
            >
              <AddIcon />
              {isSpaTenant ? t('spa.planner.menu', { defaultValue: 'SPA Planner' }) : t('reservation')}
            </Button>
          </Box>
        </Box>

        <Grid container rowSpacing={2} sx={{ justifyContent: 'flex-start', flex: 1, minHeight: 0 }}>
          <Grid xs={12} display={"flex"} justifyContent={"flex-start"} item>
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <Select
                value={selectedStanzaId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedStanzaId(value);
                  localStorage.setItem('selectedStanzaId', value);
                }}
                displayEmpty
                variant="outlined"
                renderValue={(selected) => {
                  if (selected === 'ALL') return <span style={{ color: '#888' }}>{t('allRoom')}</span>;
                  const stanza = stanze.find(s => s.id === selected);
                  return stanza ? stanza.name : '';
                }}
              >
                <MenuItem value="ALL">{t('allRoom')}</MenuItem>
                {activeStanze.map((sala) => (
                  <MenuItem key={sala.id} value={sala.id}>{sala.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Box
            onScroll={handleScroll}
            sx={{
              width: "100%",
              flexBasis: "100%",
              flexGrow: 1,
              minHeight: 0,
              overflowY: "auto",
              pr: 0.5,
              touchAction: "pan-y",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Grid container display={"flex"} justifyContent={"flex-start"} width={"100%"} spacing={1.25}>
              {tavoliFiltratiPerNome.map((tavolo) => (
                <TableCard
                  key={tavolo.id}
                  tavolo={tavolo}
                  setSelectedTavolo={setSelectedTavolo}
                  setOpenComanda={setOpenComanda}
                  getColor={getColor}
                  scrollTick={scrollTick}
                  onOpenComanda={async (tavolo) => {
                    if (isSpaTenant) {
                      openSpaPlannerForCabin(tavolo);
                      return;
                    }
                    const canOpen = await canOpenTableOrder(tavolo);
                    if (!canOpen) return;
                    orderSentRef.current = false;
                    tableWasOccupiedRef.current = false;
                    setSelectedTavolo(tavolo);
                    setOpenComanda(true);
                  }}
                  compact
                />
              ))}
            </Grid>
          </Box>
        </Grid>

        {openComanda && (
          <Dialog
            open={openComanda}
            fullWidth
            maxWidth={false}
            PaperProps={{
              sx: {
                width: { xs: '100vw', md: '95vw' },
                maxWidth: '1800px',
                height: { xs: '100dvh', md: 'auto' },
                maxHeight: { xs: '100dvh', md: '95dvh' },
                m: { xs: 0, md: 2 },
                borderRadius: { xs: 0, md: 2 },
                overflowX: 'hidden',
              },
            }}
            onClose={handleCloseComanda}
          >
            <DialogContent sx={{
              padding: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'hidden',
              overflowX: 'hidden',
              touchAction: 'pan-x pan-y pinch-zoom',
              WebkitOverflowScrolling: 'touch',
            }}>
              <SelezioneComanda
                open={openComanda}
                tavolo={selectedTavolo}
                onClose={handleCloseComanda}
                onTableOccupied={() => { tableWasOccupiedRef.current = true; }}
                onOrderSent={() => { orderSentRef.current = true; }}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    );
  }

  if (loading) return <CircularProgress sx={{ mt: 10, mx: 'auto', display: 'block' }} />;

  const mobileToolbarButtonSx = {
    minWidth: 0,
    width: { xs: 'calc(50% - 4px)', sm: 'auto' },
    flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' },
    whiteSpace: 'normal',
    lineHeight: 1.2,
    py: { xs: 1, sm: 0.75 },
  };

  return (
    <Box
      padding={{ xs: 1.25, sm: `${padding}px` }}
      mb={2}
      width="100%"
      maxWidth="100%"
      sx={{
        boxSizing: 'border-box',
        overflowX: 'clip',
        overflowY: 'visible',
        minHeight: { xs: 'calc(100dvh - 48px)', md: 'auto' },
        touchAction: 'pan-x pan-y pinch-zoom',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box
        gap={{ xs: 1.25, sm: 2 }}
        mt={{ xs: 1, sm: 0 }}
        mb={{ xs: 2, md: 3 }}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        sx={{ maxWidth: '100%', overflowX: 'hidden' }}
      >
        <Box display="flex" alignItems={{ xs: 'stretch', sm: 'center' }} gap={{ xs: 1, sm: 2 }} flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: '100%' }}>
          <Typography gutterBottom sx={{
            fontFamily: "Helvetica Neue", fontSize: "20px", fontStyle: "normal",
            fontWeight: "700", lineHeight: "175%", letterSpacing: "0.4px",
            width: { xs: '100%', sm: 'auto' },
            mb: { xs: 0, sm: undefined },
          }}>
            {getTenantTranslation(t, tenantType, 'tablesMap')}
          </Typography>

          <Button
            color='secondary'
            variant="contained"
            sx={{
              ml: { xs: 0, sm: "2px" },
              ...mobileToolbarButtonSx,
              backgroundColor: theme.palette.mode === 'dark' ? '#78BD6A' : '#125605',
              '&:hover': { backgroundColor: theme.palette.customPagination.secondary },
            }}
            onClick={() => isSpaTenant ? navigate('/spa/planner') : navigate('/prenotazione')}
          >
            <AddIcon />
            {isSpaTenant ? t('spa.planner.menu', { defaultValue: 'SPA Planner' }) : t('reservation')}
          </Button>

          <Button
            variant="contained"
            sx={{ ...{ ml: { xs: 0, sm: "2px" } }, ...theme.mappatavoli.add_edit_button, ...mobileToolbarButtonSx }}
            onClick={() => setOpenTavolo(true)}
          >
            <AddIcon />
            {getTenantTranslation(t, tenantType, 'addTable')}
          </Button>

          <Button
            variant="contained"
            sx={{ ...{ ml: { xs: 0, sm: "2px" } }, ...theme.mappatavoli.add_edit_button, ...mobileToolbarButtonSx }}
            onClick={() => setOpenSala(true)}
          >
            <AddIcon />
            {getTenantTranslation(t, tenantType, 'addRoom')}
          </Button>

          <Button
            variant="contained"
            sx={{ ...{ ml: { xs: 0, sm: "2px" } }, ...theme.mappatavoli.add_edit_button, ...mobileToolbarButtonSx }}
            onClick={() => setOpenEditSala(true)}
          >
            <ModeEditOutlineOutlinedIcon />
            {getTenantTranslation(t, tenantType, 'editRoom')}
          </Button>
        </Box>

        <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button
            color='secondary'
            variant="contained"
            sx={{
              ...{
                mx: { xs: 0, sm: 1 },
                '&:hover': { backgroundColor: theme.palette.customPagination.secondary },
                alignSelf: "end",
                width: { xs: 'calc(50% - 4px)', sm: 'auto' },
                minWidth: 0,
                whiteSpace: 'normal',
                lineHeight: 1.2,
              },
              ...theme.mappatavoli.delete_button
            }}
            onClick={() => setOpenDeleteSala(true)}
          >
            <DeleteForeverOutlinedIcon />
            {getTenantTranslation(t, tenantType, 'deleteRoom')}
          </Button>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex', gap: 1, paddingY: "16px", flexWrap: 'wrap',
        mb: 2, borderBottom: "1px solid rgba(169, 169, 169, 0.20)",
        maxWidth: '100%',
        overflowX: 'hidden',
      }}>
        {(!isConferenceTenant && vistaCorrente === 'dettagliata'
          ? activeStanze
          : [{ id: 'ALL', name: t('all') }, ...activeStanze]
        ).map((sala) => {
          const isSelected = selectedStanzaId === sala.id;
          return (
            <Button
              key={sala.id}
              variant={isSelected ? 'contained' : 'outlined'}
              onClick={() => {
                setSelectedStanzaId(sala.id);
                localStorage.setItem('selectedStanzaId', sala.id);
              }}
              sx={{
                backgroundColor: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.mode === 'light' ? '#fafafa' : '#333',
                border: theme.palette.mode === 'light' ? '#fafafa' : '#131313',
                color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: isSelected ? theme.palette.primary.light : theme.palette.action.hover,
                  border: theme.palette.mode === 'light' ? '#fafafa' : '#131313',
                },
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              {sala.name}
            </Button>
          );
        })}
        <TextField
          value={cercaTavolo}
          onChange={handleSearch}
          placeholder={t('search')}
          size="small"
          variant="outlined"
          InputProps={{ startAdornment: <Search sx={{ mr: 1 }} /> }}
          sx={{ ml: { xs: 0, sm: 'auto' }, minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2, maxWidth: '100%', overflowX: 'hidden' }}>
        <Typography sx={{
          fontFamily: "Helvetica Neue", fontSize: "20px", fontStyle: "normal",
          fontWeight: "700", lineHeight: "175%", letterSpacing: "0.4px",
        }}>
          {isConferenceTenant || vistaCorrente === 'standard' ? t('Standard') : t('Detailed')}
        </Typography>

        <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {vistaCorrente === 'standard' && (
            <Button
              color="primary"
              variant="contained"
              sx={{ ml: { xs: 0, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
              onClick={() => setEditTable(!editTable)}
            >
              <ModeEditOutlineOutlinedIcon />
              {getTenantTranslation(t, tenantType, 'editRoom')}
            </Button>
          )}
          {!isConferenceTenant && (
            <Button
              onClick={() => {
                setVistaCorrente(vistaCorrente === 'standard' ? 'dettagliata' : 'standard');
                setEditTable(false);
              }}
              sx={{
                display: "flex", padding: "8px 22px", flexDirection: "column",
                justifyContent: "center", alignItems: "center", borderRadius: "4px",
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: theme.palette.mode === 'dark' ? '#549AEF' : '#0052cc',
                '&:hover': { backgroundColor: theme.palette.customPagination.secondary },
                color: theme.palette.primary.contrastText,
              }}
            >
              <Box display={'flex'} alignItems={'center'} flexDirection={"row"}>
                {vistaCorrente === 'dettagliata'
                  ? (<><PinDropOutlined sx={{ display: "flex", height: "24px", minHeight: "24px" }} /> {t('Standard')}</>)
                  : (<><GridViewOutlined sx={{ display: "flex", height: "24px", minHeight: "24px" }} /> {t('Detailed')}</>)
                }
              </Box>
            </Button>
          )}
        </Box>
      </Box>

      {isConferenceTenant || vistaCorrente === 'standard' ? (
        <VistaStandard
          tavoliVisibili={tavoliFiltratiPerNome}
          getColor={getColor}
          editTable={editTable}
          onCanOpenTableOrder={isSpaTenant ? null : canOpenTableOrder}
          onOpenSpaPlanner={isSpaTenant ? openSpaPlannerForCabin : null}
          onTablesChanged={fetchAllData}
        />
      ) : (
        <VistaDettagliata
          tavoliVisibili={tavoliFiltratiPerNome}
          getColor={getColor}
          allTables={tavoliInSaleAttive}
          onCanOpenTableOrder={canOpenTableOrder}
        />
      )}

      <ModalTavolo open={openTavolo} onClose={() => setOpenTavolo(false)} onSuccess={fetchTavoli} />
      <ModalSala open={openSala} onClose={() => setOpenSala(false)} onSuccess={fetchStanze} />
      <ModalEditSala open={openEditSala} onClose={() => setOpenEditSala(false)} onSuccess={fetchStanze} />
      {openDeleteSala && (
        <ModalDeleteSala open={openDeleteSala} onClose={() => setOpenDeleteSala(false)} />
      )}
      <Dialog
        open={spaReservationChoice.open}
        onClose={() => setSpaReservationChoice({ open: false, tavolo: null, isReservationClient: true, partySize: 1 })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("confirmCovers", { defaultValue: "Confirm Participants" })} - {spaReservationChoice.tavolo?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Box px={2} py={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {spaReservationChoice.tavolo?.nomeCliente} - {spaReservationChoice.tavolo?.oraPrenotazione}
            </Typography>
            <Box pb={1.5} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="body1">
                {t("confirmClientQuestion", { defaultValue: "Confirm that this is the reservation client?" })}
              </Typography>
              <RadioGroup
                row
                value={String(spaReservationChoice.isReservationClient)}
                onChange={(event) => {
                  const isReservationClient = event.target.value === 'true';
                  const reservedPartySize = Number(spaReservationChoice.tavolo?.prenotazione?.partySize || spaReservationChoice.tavolo?.numeroOspiti || 1);
                  const fallbackPartySize = Number(spaReservationChoice.tavolo?.maxSeats || reservedPartySize || 1);
                  setSpaReservationChoice((current) => ({
                    ...current,
                    isReservationClient,
                    partySize: isReservationClient
                      ? (Number.isInteger(reservedPartySize) && reservedPartySize > 0 ? reservedPartySize : 1)
                      : (Number.isInteger(fallbackPartySize) && fallbackPartySize > 0 ? fallbackPartySize : 1),
                  }));
                }}
              >
                <FormControlLabel value={false} control={<Radio />} label={t('no')} />
                <FormControlLabel value={true} control={<Radio />} label={t('yes')} />
              </RadioGroup>
            </Box>
            <Box pb={1} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="body1">
                {t("confirmSeats", { defaultValue: "Confirm participants" })}
              </Typography>
              <TextField
                type="number"
                size="small"
                value={spaReservationChoice.partySize}
                onChange={(event) => {
                  const next = Math.max(1, Number(event.target.value || 1));
                  setSpaReservationChoice((current) => ({ ...current, partySize: next }));
                }}
                inputProps={{ min: 1 }}
                sx={{ width: 90 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpaReservationChoice({ open: false, tavolo: null, isReservationClient: true, partySize: 1 })}>
            {t("spa.common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={continueSpaReservedCabinFlow}
          >
            {t("continue", { defaultValue: "Continue" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TableCard = React.memo(function TableCard({ tavolo, setSelectedTavolo, setOpenComanda, getColor, scrollTick, onOpenComanda, compact = false }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tenantType = localStorage.getItem('tenantType');

  const textRef = useRef(null);
  const [scroll, setScroll] = useState(false);
  const [duration, setDuration] = useState(6);

  useLayoutEffect(() => {
    if (!textRef.current) return;
    const el = textRef.current;
    setScroll(el.scrollWidth >= el.clientWidth);
    if (el.scrollWidth > el.clientWidth) {
      const distance = el.scrollWidth - el.clientWidth;
      setDuration(Math.max(5, distance / 80));
    } else {
      setScroll(false);
    }
  }, [tavolo.name, scrollTick]);

  return (
    <Grid item xs={compact ? 6 : 4} sm={compact ? 4 : 3} md={compact ? 3 : 3} lg={compact ? 2 : 3} xl={compact ? 2 : 3}>
      <Card
        sx={{
          p: 1,
          color: theme.palette.primary.contrastText,
          textAlign: 'center',
          backgroundColor: getColor(tavolo.status),
          cursor: 'pointer',
          minHeight: tavolo.hasPrenotazione || tavolo.note ? '136px' : '108px',
          maxHeight: tavolo.hasPrenotazione || tavolo.note ? '170px' : '112px',
          width: '100%',
          minWidth: 0,
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant='subtitle1'
          ref={textRef}
          sx={{
            width: "100%",
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: "16px",
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
            ...(scroll && {
              animation: `marquee ${duration}s linear infinite`,
              animationDelay: "2s",
              "@keyframes marquee": {
                "0%": { transform: "translateX(0%)" },
                "10%": { transform: "translateX(0%)" },
                "90%": { transform: `translateX(-${textRef.current.scrollWidth - textRef.current.clientWidth}px)` },
                "100%": { transform: `translateX(-${textRef.current.scrollWidth - textRef.current.clientWidth}px)` },
              }
            }),
            ...theme.checkout.animation_typography,
          }}
        >
          {tavolo.name}
        </Typography>

        <Typography variant='caption'>{t('stato')}: {t(tavolo.status)}</Typography>

        <Typography variant="caption" sx={{
          textAlign: "center", fontFamily: "Helvetica Neue",
          fontSize: "9px", fontWeight: "500", lineHeight: "160%", letterSpacing: "0.217px",
        }}>
          {tavolo.status === "LIBERO"
            ? (getTenantTranslation(t, tenantType, "seatNumber") + ": " + tavolo.maxSeats)
            : (getTenantTranslation(t, tenantType, "occupiedSeats") + ": " + tavolo.takenSeats + "/" + tavolo.maxSeats)}
        </Typography>

        {tavolo.note && (
          <Typography
            variant="caption"
            title={tavolo.note}
            sx={{ px: 0.5, fontSize: '9px', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {t('notes')}: {tavolo.note}
          </Typography>
        )}

        {tavolo.status !== "LIBERO" && tavolo.hasPrenotazione && (
          <Box sx={{
            mt: 0.5, px: 0.5, py: 0.25,
            borderRadius: '4px',
            backgroundColor: 'rgba(0,0,0,0.18)',
            textAlign: 'left',
          }}>
            <Typography variant="caption" sx={{ fontSize: "9px", fontWeight: 700, display: 'block', lineHeight: 1.4 }}>
              🕐 {tavolo.oraPrenotazione} · {tavolo.nomeCliente}
            </Typography>
            {tavolo.numeroOspiti !== '—' && (
              <Typography variant="caption" sx={{ fontSize: "9px", display: 'block', lineHeight: 1.3 }}>
                {t('guests')}: {tavolo.numeroOspiti}
              </Typography>
            )}
          </Box>
        )}

        <Button
          variant='contained'
          onClick={() => onOpenComanda ? onOpenComanda(tavolo) : (setSelectedTavolo(tavolo), setOpenComanda(true))}
          sx={{
            display: 'flex',
            width: '100%',
            mt: 'auto',
            padding: '8px 0',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
            borderRadius: '4px',
            color: theme.palette.text.primary,
            background: theme.palette.mode === 'light' ? '#E9F3FF' : '#192F49',
          }}
        >
          {t('show')}
          <VisibilityOutlined />
        </Button>
      </Card>
    </Grid>
  );
});

export default MappaTavoli;
